import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import useAdminDraftGuard from '../useAdminDraftGuard';
import ReactQuillEditor from '../../../components/Editor/ReactQuillEditor';
import RepresentativeImageSelector, {
  extractImageUrls,
  getRepresentativeImageUrl,
} from '../../../components/common/RepresentativeImageSelector';

const emptyForm = {
  title: '',
  titleEn: '',
  content: '',
  contentEn: '',
  imageUrl: '',
  memberGateAfterBlock: '',
  memberGateAfterBlockEn: '',
  isHidden: false,
  isSponsored: false,
};

export const contentBlocks = (html = '') => {
  if (typeof DOMParser === 'undefined') return [];
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  return Array.from(body.children).map((element, index) => ({
    index: index + 1,
    label:
      element.textContent?.replace(/\s+/g, ' ').trim().slice(0, 45) ||
      (element.tagName === 'IMG' ? '이미지' : element.tagName.toLowerCase()),
  }));
};

function GateSelect({ html, label, value, onChange }) {
  const blocks = useMemo(() => contentBlocks(html), [html]);
  const labelId = useId();
  return (
    <FormControl fullWidth>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <MenuItem value="">전체 공개</MenuItem>
        <MenuItem value={0}>제목 직후부터 회원만 공개</MenuItem>
        {blocks.map((block) => (
          <MenuItem key={block.index} value={block.index}>
            {block.index}번째 블록 뒤 · {block.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function FeedManagement() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editorLocale, setEditorLocale] = useState('ko');
  const [form, setForm] = useState(emptyForm);
  const draft = useAdminDraftGuard(form, { enabled: formOpen, busy });
  const requestClose = () => {
    if (!draft.confirmDiscard()) return;
    setFormOpen(false);
  };
  const hasContentImages = useMemo(() => extractImageUrls(form.content).length > 0, [form.content]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
      const response = await api.getAuth(`/admin/app/feeds${params}`);
      setItems(response.data.data ?? []);
    } catch (failure) {
      setError(failure.message || '피드 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      void load();
    }, 250);
    return () => clearTimeout(timeout);
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setEditorLocale('ko');
    setError('');
    setFormOpen(true);
  };
  const openEdit = (item) => {
    setEditingId(item.id);
    const initial = {
      title: item.title ?? '',
      titleEn: item.titleEn ?? '',
      content: item.content ?? '',
      contentEn: item.contentEn ?? '',
      imageUrl: item.imageUrl ?? '',
      memberGateAfterBlock: item.memberGateAfterBlock ?? '',
      memberGateAfterBlockEn: item.memberGateAfterBlockEn ?? '',
      isHidden: Boolean(item.isHidden),
      isSponsored: Boolean(item.isSponsored),
    };
    setForm(initial);
    setEditorLocale('ko');
    setError('');
    setFormOpen(true);
  };
  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const save = async () => {
    if (busy) return;
    // An explicitly selected or entered cover remains authoritative. Only fall
    // back to the first body image when the cover field is empty.
    const imageUrl = form.imageUrl.trim() || getRepresentativeImageUrl(form.content, '', '');
    if (!form.title.trim() || !form.content.trim() || !imageUrl.trim()) {
      setError('한국어 제목·본문과 대표 이미지를 모두 입력해주세요.');
      return;
    }
    const payload = {
      ...form,
      title: form.title.trim(),
      titleEn: form.titleEn.trim() || null,
      contentEn: form.contentEn.trim() || null,
      imageUrl: imageUrl.trim(),
      memberGateAfterBlock:
        form.memberGateAfterBlock === '' ? null : Number(form.memberGateAfterBlock),
      memberGateAfterBlockEn:
        form.contentEn.trim() && form.memberGateAfterBlockEn !== ''
          ? Number(form.memberGateAfterBlockEn)
          : null,
    };
    setBusy(true);
    setError('');
    try {
      if (editingId) await api.putAuth(`/admin/app/feeds/${editingId}`, payload);
      else await api.postAuth('/admin/app/feeds', payload);
      setFormOpen(false);
      await load();
    } catch (failure) {
      setError(failure.message || '피드를 저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };
  const remove = async (item) => {
    if (!window.confirm(`“${item.title}” 피드를 삭제할까요? 이 작업은 되돌릴 수 없습니다.`)) return;
    try {
      await api.deleteAuth(`/admin/app/feeds/${item.id}`);
      await load();
    } catch (failure) {
      setError(failure.message || '피드를 삭제하지 못했습니다.');
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
        <Box>
          <Typography variant="h6">피드</Typography>
          <Typography variant="body2" color="text.secondary">
            앱 피드의 본문·대표 이미지와 비회원 공개 범위를 관리합니다.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openCreate}>
          새 피드 작성
        </Button>
      </Stack>
      <TextField
        label="제목 검색"
        size="small"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {error && !formOpen ? <Alert severity="error">{error}</Alert> : null}
      {loading ? <Typography>불러오는 중…</Typography> : null}
      {!loading && !items.length ? <Alert severity="info">등록된 피드가 없습니다.</Alert> : null}
      <Stack spacing={1.25}>
        {items.map((item) => (
          <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                component="img"
                src={item.imageUrl}
                alt=""
                sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={700} noWrap>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.createdAt} · {item.isHidden ? '숨김' : '공개'} ·{' '}
                  {item.memberGateAfterBlock == null
                    ? '전체 공개'
                    : `${item.memberGateAfterBlock}개 블록 공개 후 회원 잠금`}
                </Typography>
              </Box>
              <Button onClick={() => openEdit(item)}>수정</Button>
              <Button color="error" onClick={() => remove(item)}>
                삭제
              </Button>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Dialog fullWidth maxWidth="md" open={formOpen} onClose={requestClose}>
        <DialogTitle>{editingId ? '피드 수정' : '새 피드 작성'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ pt: 1 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              required
              label="제목 (한국어)"
              value={form.title}
              onChange={(event) => change('title', event.target.value)}
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label="제목 (영어, 선택)"
              value={form.titleEn}
              onChange={(event) => change('titleEn', event.target.value)}
              inputProps={{ maxLength: 255 }}
            />
            <Tabs value={editorLocale} onChange={(_, value) => setEditorLocale(value)}>
              <Tab value="ko" label="한국어 본문" />
              <Tab value="en" label="영어 본문 (선택)" />
            </Tabs>
            <ReactQuillEditor
              feedLinks
              toolbarTop={0}
              key={editorLocale}
              value={editorLocale === 'ko' ? form.content : form.contentEn}
              onChange={(value) => change(editorLocale === 'ko' ? 'content' : 'contentEn', value)}
            />
            {editorLocale === 'ko' ? (
              <>
                <GateSelect
                  html={form.content}
                  label="비회원 공개 범위 (한국어)"
                  value={form.memberGateAfterBlock}
                  onChange={(value) => change('memberGateAfterBlock', value)}
                />
                {hasContentImages ? (
                  <RepresentativeImageSelector
                    content={form.content}
                    value={form.imageUrl}
                    onChange={(value) => change('imageUrl', value)}
                  />
                ) : null}
              </>
            ) : (
              <GateSelect
                html={form.contentEn}
                label="비회원 공개 범위 (영어)"
                value={form.memberGateAfterBlockEn}
                onChange={(value) => change('memberGateAfterBlockEn', value)}
              />
            )}
            <TextField
              required
              label="대표 이미지 URL"
              helperText="본문 이미지 중 선택하거나 http(s) 이미지 주소를 입력하세요. GIF도 사용할 수 있습니다."
              value={form.imageUrl}
              onChange={(event) => change('imageUrl', event.target.value)}
            />
            {form.imageUrl ? (
              <Box
                component="img"
                src={form.imageUrl}
                alt="대표 이미지 미리보기"
                sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1 }}
              />
            ) : null}
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isSponsored}
                  onChange={(event) => change('isSponsored', event.target.checked)}
                />
              }
              label="광고 표시 (앱 피드 목록 날짜 옆)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isHidden}
                  onChange={(event) => change('isHidden', event.target.checked)}
                />
              }
              label="앱에서 숨김"
            />
            <Alert severity="info">
              잠금 위치 이후 본문은 비회원 API 응답에도 포함되지 않습니다. 로그인 회원은 전체 본문을
              볼 수 있습니다.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={requestClose}>
            취소
          </Button>
          <Button disabled={busy} variant="contained" onClick={save}>
            {busy ? '저장 중…' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
